import { AxiosError } from "axios";
import { MESSAGES } from "@/constants/messages";

/**
 * Shape do corpo de erro padrão da API (espelha o `ErrorResponseDTO` do back-end).
 * - `message`: mensagem de negócio/domínio (ex.: "O documento informado é inválido")
 * - `errors`: validação por campo (ex.: { document: "Documento é obrigatório" }) — apenas 400
 * - `error`: código/categoria do erro (ex.: "INVALID_DOCUMENT", "Conflict")
 * - `details`: payload extra (ex.: indisponibilidade de estoque)
 */
export interface ApiErrorBody {
  timestamp?: string;
  status?: number;
  error?: string;
  message?: string;
  path?: string;
  errors?: Record<string, string>;
  details?: Record<string, unknown>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * Mensagens genéricas do back-end que escondem a causa real: quando vierem,
 * preferimos o mapa `errors` (que carrega o motivo por campo).
 */
const GENERIC_BACKEND_MESSAGES = new Set([
  "validation failed for one or more fields",
]);

/** Extrai o corpo de erro padrão de qualquer valor lançado, se existir. */
export function getApiErrorBody(error: unknown): ApiErrorBody | undefined {
  if (error instanceof AxiosError) {
    return isRecord(error.response?.data)
      ? (error.response?.data as ApiErrorBody)
      : undefined;
  }
  // Tolera objetos com o mesmo formato de AxiosError (ex.: erro re-lançado)
  if (isRecord(error) && isRecord(error.response)) {
    const data = (error.response as Record<string, unknown>).data;
    return isRecord(data) ? (data as ApiErrorBody) : undefined;
  }
  return undefined;
}

/**
 * Retorna o mapa de erros por campo (validação 400), se houver.
 * Útil para exibir erros inline em formulários (`form.setError`).
 */
export function getApiFieldErrors(
  error: unknown,
): Record<string, string> | undefined {
  const errors = getApiErrorBody(error)?.errors;
  if (errors && isRecord(errors) && Object.keys(errors).length > 0) {
    return errors as Record<string, string>;
  }
  return undefined;
}

/** Junta as mensagens de validação por campo em um texto legível e sem repetição. */
function formatFieldErrors(errors: Record<string, string>): string | undefined {
  const messages = Object.values(errors)
    .map((m) => (typeof m === "string" ? m.trim() : ""))
    .filter(Boolean);
  if (messages.length === 0) return undefined;
  return Array.from(new Set(messages)).join(" · ");
}

/**
 * Extrai a mensagem de erro mais específica e amigável de QUALQUER erro vindo
 * da camada de API, para ser exibida ao usuário (toast).
 *
 * Prioridade:
 *   1. Erros de validação por campo (`errors`) — carregam o motivo real (CPF inválido, campos obrigatórios)
 *   2. Mensagem de negócio do back-end (`message`)
 *   3. Código/categoria do erro (`error`)
 *   4. Falha de rede / timeout (sem resposta HTTP)
 *   5. `fallback` informado pelo chamador
 *
 * @example
 *   catch (err) {
 *     toast.error(getApiErrorMessage(err, "Erro ao criar cliente"));
 *   }
 */
export function getApiErrorMessage(
  error: unknown,
  fallback: string = MESSAGES.ERROR.GENERIC,
): string {
  // Sem resposta HTTP → falha de rede, timeout ou cancelamento
  if (error instanceof AxiosError && !error.response) {
    if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
      return "A requisição demorou demais e foi cancelada. Tente novamente.";
    }
    if (error.code === "ERR_CANCELED") {
      return fallback;
    }
    return MESSAGES.ERROR.NETWORK;
  }

  const body = getApiErrorBody(error);

  if (body) {
    // 1. Validação por campo (motivo real, ex.: CPF/documento inválido)
    if (isRecord(body.errors)) {
      const fieldMessage = formatFieldErrors(
        body.errors as Record<string, string>,
      );
      if (fieldMessage) return fieldMessage;
    }

    // 2. Mensagem de negócio do back-end, desde que não seja a genérica
    if (typeof body.message === "string" && body.message.trim()) {
      const normalized = body.message.trim();
      if (!GENERIC_BACKEND_MESSAGES.has(normalized.toLowerCase())) {
        return normalized;
      }
    }

    // 3. Código/categoria do erro como último recurso do corpo
    if (typeof body.error === "string" && body.error.trim()) {
      return body.error.trim();
    }
  }

  // 4. Erro nativo (não-Axios) com mensagem própria
  if (
    error instanceof Error &&
    !(error instanceof AxiosError) &&
    error.message.trim()
  ) {
    return error.message.trim();
  }

  return fallback;
}

export async function resolveBlobApiError(error: unknown): Promise<unknown> {
  if (!isRecord(error) || !isRecord(error.response)) return error;

  const response = error.response as Record<string, unknown>;
  const data = response.data;
  if (!(data instanceof Blob)) return error;

  try {
    const parsed: unknown = JSON.parse(await data.text());
    if (!isRecord(parsed)) return error;
    response.data = parsed;
    return error;
  } catch {
    return error;
  }
}
